/**
 * ViewControl Component
 * Handles different view modes and decides what to render based on openType and campaignOnly
 *
 * View modes:
 * - campaignOnly.type='tournaments': InGameHeader + TournamentsOnlyLayout
 * - campaignOnly.type='prizePop': (future) InGameHeader + PrizePopOnlyLayout
 * - openType=ingame (without campaign mode): InGameHeader + Lobby
 * - openType=fullscreen (without campaign mode): Lobby only
 *
 * The campaignOnly setting supports passing a campaignID to navigate directly to a specific
 * item in the respective carousel (tournament, prizePop, etc.).
 */

import React, { memo, useEffect, useRef, type ComponentType } from 'react';

import {
  LOBBY_SECTION_NAME,
  PRICE_POP_SECTION_NAME,
  REBATE_SECTION_NAME,
  TOURNAMENTS_SECTION_NAME,
  type INGAME_CAMPAIGN_TABS,
} from '@repo/common';
import {
  CAMPAIGN_TYPE_TO_SECTION,
  PRIZE_POP_CAMPAIGN_TYPE,
  REBATE_CAMPAIGN_TYPE,
  SECTION_TO_CAMPAIGN_TYPE,
  TOURNAMENTS_CAMPAIGN_TYPE,
  useCampaignSectionGate,
  type CampaignTypes,
} from 'features/campaigns';
import { IngameCampaignNavigation } from 'features/campaigns/_exports/ui';
import { LOBBY_INGAME_POPUP_TITLE_BAR_HEIGHT } from '../../constants';
import { useTrackerManagerHook } from '../../core/services/managers/TrackerManager';
import { useAppStore } from '../../store/app/appStore';
import type { ParsedSettingsResponse } from '../../types/api/api.types';
import { useCampaignTabNavigation } from '../CampaignOnlyLayouts/hooks/useCampaignTabnavigation';
import PricePopOnlyLayout from '../CampaignOnlyLayouts/PrizePopOnlyLayout/PrizePopOnlyLayout';
import RebateOnlyLayout from '../CampaignOnlyLayouts/RebateOnlyLayout/RebateOnlyLayout';
import TournamentsOnlyLayout from '../CampaignOnlyLayouts/TournamentsOnlyLayout/TournamentsOnlyLayout';
import InGameHeader from '../InGameHeader';
import Lobby from '../Lobby';

interface ViewControlProps {
  settingsData?: ParsedSettingsResponse;
}

interface CampaignOnlyLayoutConfig {
  Layout: ComponentType<{ campaignID?: string }>;
  tab: keyof typeof INGAME_CAMPAIGN_TABS;
}

const CAMPAIGN_ONLY_LAYOUTS: Partial<Record<CampaignTypes, CampaignOnlyLayoutConfig>> = {
  [TOURNAMENTS_CAMPAIGN_TYPE]: { Layout: TournamentsOnlyLayout, tab: TOURNAMENTS_SECTION_NAME },
  [PRIZE_POP_CAMPAIGN_TYPE]: { Layout: PricePopOnlyLayout, tab: PRICE_POP_SECTION_NAME },
  [REBATE_CAMPAIGN_TYPE]: { Layout: RebateOnlyLayout, tab: REBATE_SECTION_NAME },
};

const ViewControl: React.FC<ViewControlProps> = ({ settingsData: _settingsData }) => {
  // Get settings and actions from app store
  const openType = useAppStore((state) => state.settings.openType);
  const campaignOnly = useAppStore((state) => state.settings.campaignOnly);
  const selectedSection = useAppStore((state) => state.selectedSection);
  const viewMode = useAppStore((state) => state.viewMode);
  const setSelectedSection = useAppStore((state) => state.setSelectedSection);
  const setViewMode = useAppStore((state) => state.setViewMode);

  const { isCampaignEnabled } = useCampaignSectionGate();
  const campaignOnlyConfig =
    campaignOnly?.type && isCampaignEnabled(CAMPAIGN_TYPE_TO_SECTION[campaignOnly.type]) === true
      ? CAMPAIGN_ONLY_LAYOUTS[campaignOnly.type]
      : undefined;

  const isCampaignOnlyMode = !!campaignOnlyConfig;

  // Track previous campaign-only mode value to detect changes
  const prevCampaignOnlyModeRef = useRef<boolean | null>(null);
  // Reset to default lobby state when exiting campaign-only mode entirely (not when
  // switching between campaign types, e.g. tournaments <-> prize pop, which should
  // keep viewMode='single' with the newly selected campaign section).
  useEffect(() => {
    const prevCampaignOnlyMode = prevCampaignOnlyModeRef.current;

    if (prevCampaignOnlyMode === true && isCampaignOnlyMode === false) {
      const needsReset = !!SECTION_TO_CAMPAIGN_TYPE[selectedSection] || viewMode === 'single';

      if (needsReset) {
        setSelectedSection(LOBBY_SECTION_NAME);
        setViewMode('multi');
      }
    }

    prevCampaignOnlyModeRef.current = isCampaignOnlyMode;
  }, [isCampaignOnlyMode, selectedSection, viewMode, setSelectedSection, setViewMode]);

  useTrackerManagerHook('ViewControl', [viewMode, selectedSection, openType]);

  // Rendered once here (not inside each *OnlyLayout) so the nav is a single persistent
  // component whose activeTab prop toggles on switch, instead of two components being
  // mounted/unmounted — that's what lets the active-indicator CSS transition animate
  // instead of just snapping into place on a fresh DOM node. Kept as one return (rather
  // than a return per branch) so there's only one JSX shape for IngameCampaignNavigation
  // to stay at a stable sibling position in — two copies could drift out of sync and
  // silently reintroduce the remount.
  const handleCampaignTabChange = useCampaignTabNavigation();

  if (campaignOnlyConfig) {
    const { Layout, tab } = campaignOnlyConfig;

    return (
      <>
        {openType === 'ingame' && <InGameHeader />}
        <Layout campaignID={campaignOnly?.campaignID} />
        <IngameCampaignNavigation
          activeTab={tab}
          onTabChange={handleCampaignTabChange}
        />
      </>
    );
  }

  // openType=ingame + no campaign-only mode: Show InGameHeader + Lobby
  if (openType === 'ingame') {
    return (
      <>
        <InGameHeader />
        <div
          style={{
            position: 'relative',
            top: LOBBY_INGAME_POPUP_TITLE_BAR_HEIGHT,
            height: `calc(100% - ${LOBBY_INGAME_POPUP_TITLE_BAR_HEIGHT})`,
          }}
        >
          <Lobby />
        </div>
      </>
    );
  }

  // openType=fullscreen + no campaign-only mode: Show Lobby only
  return <Lobby />;
};

export default memo(ViewControl);